import * as React from "react";
import { Slot } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  [
    "inline-flex w-fit shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full",
    "px-2 py-0.5 text-[11.5px] font-medium leading-[18px] tracking-[0.01em]",
    "[&>svg]:pointer-events-none [&>svg]:size-3",
    "transition-colors duration-150",
  ],
  {
    variants: {
      variant: {
        /** Neutral: keywords, generic metadata */
        neutral: "border border-line bg-surface-2 text-ink-2",
        /** Category: soft accent tint */
        category: "border border-[rgb(167_139_250/0.22)] bg-accent-tint text-[#cbb8ff]",
        /** Trait: indigo-leaning */
        trait: "border border-[rgb(99_102_241/0.25)] bg-[rgb(99_102_241/0.12)] text-[#b9b7ff]",
        /** Keyword: quiet outline */
        keyword: "border border-line text-ink-3",
        accent: "bg-accent-strong text-white",
        success: "border border-[rgb(90_211_166/0.3)] bg-[rgb(90_211_166/0.12)] text-success",
        warning: "border border-[rgb(232_184_109/0.3)] bg-[rgb(232_184_109/0.12)] text-ember",
        danger: "border border-[rgb(240_100_122/0.3)] bg-[rgb(240_100_122/0.12)] text-danger",
        count: "bg-accent text-void font-mono text-[10.5px] px-1.5 min-w-[18px] leading-[16px]",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";
  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
